<script setup>
import { storeToRefs } from "pinia";
import { onMounted } from "vue";

import { BaseDropdown, BaseNotification, IconBell, isArray } from "@/shared";

import { useNotificationsStore } from "../model/notificationsStore";

const notificationsStore = useNotificationsStore();
const { notificationsList } = storeToRefs(notificationsStore);

const notifyColors = {
    attention: "#FAAD65",
    warning: "#FAAD65",
    success: "#0AA89E",
    error: "#ff4d4f",
    info: "#1090E4",
    neutral: "#939191",
};

onMounted(async () => {
    await notificationsStore.getNotifications();
});
</script>

<template>
    <BaseDropdown
        button-variant="primary"
        button-type="circle"
        button-class="notifications-dropdown"
        tooltip-text="Уведомления"
    >
        <template #button-content>
            <IconBell class="notifications-dropdown__icon" />
            <span
                v-if="notificationsList?.length > 0"
                class="notifications-dropdown__new-notifications"
            />
        </template>
        <template #dropdown-menu>
            <div class="notifications-dropdown__dropdown">
                <div class="notifications-dropdown__head">
                    <h3 class="notifications-dropdown__title">Уведомления</h3>
                    <p
                        class="notifications-dropdown__clear"
                        @click="notificationsStore.readAllNotifications"
                    >
                        Очистить
                    </p>
                </div>
                <template v-if="notificationsList?.length > 0">
                    <TransitionGroup name="fade">
                        <BaseNotification
                            v-for="notification in notificationsList"
                            class="notifications-dropdown__item"
                            :key="notification.id"
                            :color="notifyColors[notification.type]"
                            :item="notification"
                            :type="notification.type"
                            :icon="notification.icon"
                            :duration="notification.duration"
                            :date="notification.date"
                            :text="notification.text"
                            :title="notification.title"
                            :with-timer="false"
                            @close="
                                notificationsStore.readNotification(
                                    notification.id
                                )
                            "
                        >
                            <template
                                v-if="typeof notification.title === 'object'"
                                #title
                            >
                                <component :is="notification.title" />
                            </template>
                            <template
                                v-if="typeof notification.text === 'object'"
                                #text
                            >
                                <template v-if="isArray(notification.text)">
                                    <component
                                        v-for="(
                                            text, index
                                        ) in notification.text"
                                        :key="index"
                                        :is="text"
                                    />
                                </template>
                                <component v-else :is="notification.text" />
                            </template>
                        </BaseNotification>
                    </TransitionGroup>
                </template>
                <div v-else class="notifications-dropdown__empty">
                    <p>Новых уведомлений нет</p>
                </div>
            </div>
        </template>
    </BaseDropdown>
</template>

<style lang="scss" scoped>
.notifications-dropdown {
    &__new-notifications {
        position: absolute;
        top: 25%;
        right: 25%;

        width: 5px;
        height: 5px;

        background-color: var(--c-danger);
        border-radius: 50%;
    }

    &__dropdown {
        position: relative;

        min-width: 200px;
        max-height: 300px;
        padding: 50px 10px 24px;
        overflow: auto;

        background-color: var(--c-neutral-10);
        border-radius: 5px;
        box-shadow: 0 0 5px rgb(var(--c-rgb-neutral-70) / 20%);
    }

    &__head {
        position: fixed;
        top: 0;
        right: 0;
        left: 0;
        z-index: 1;

        display: flex;

        align-items: center;

        justify-content: space-between;

        width: 100%;
        padding: 10px;

        background-color: var(--c-neutral-10);
        border-radius: 5px;
        box-shadow: 0 0 5px rgb(var(--c-rgb-neutral-70) / 20%);
    }

    &__clear {
        font-size: 12px;

        cursor: pointer;

        &:hover {
            color: var(--c-danger);
        }
    }

    &__empty {
        display: flex;

        align-items: center;
        justify-content: center;

        width: 300px;
        height: 100px;
    }

    &__item {
        margin-bottom: 5px;
    }

    &__icon {
        display: block !important;

        pointer-events: none;
    }

    &:active {
        svg {
            color: var(--c-green-10) !important;
        }
    }
}

.fade-enter-active {
    animation: fade 0.5s;
}

.fade-leave-active {
    animation: fade 0.5s reverse;
}

@keyframes fade {
    0% {
        opacity: 0;
    }

    100% {
        opacity: 1;
    }
}
</style>
